
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@core/services/core/auth/auth.service';
import { BaseService } from '@core/services/core/base.service';
import { ContextService } from '@core/services/core/context.service';
import { EventsService } from '@core/services/core/events.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { BUSINESS_OPERATION_CODE_PREFIX, OperationRegisterService } from '@core/services/core/operation-register.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { AddressHelperService } from '@core/services/helpers/address-helper.service';
import { PolicyVehicleAccidentsHelperService } from '@core/services/helpers/policy-vehicle-accidents-helper.service';
import {
  AuthService as VehicleAccidentsLibraryAuthService,
  ContactPerson,
  InitializationContextService,
  LoginAction as VehicleAccidentsLoginAction,
  User as VehicleAccidentsUser,
  VehicleAccidentsEvent,
  VehicleAccidentsOperationId
} from '@mapfre/vehicle-accidents';
import { AllFeatureFlags } from '@models/mmobile';
import { PolicySinister } from '@models/policy';
import { PolicyCar, PolicyCarForm } from '@models/policy-car';
import { getBusinessVAOperationsMap, getVAOperationsMap } from '@models/va-operations';
import { isValidNie, isValidNif, validateCif } from '@utils/utils';

const VEHICLE_ACCIDENTS_DEFAULT_STREET_TYPE = 'ZZ';

@Injectable({
  providedIn: 'root'
})
export class VehicleAccidentsHelperService {

  constructor(
    public nav: NavigationHelper,
    private vaService: InitializationContextService,
    private vaAuthService: VehicleAccidentsLibraryAuthService,
    private or: OperationRegisterService,
    private base: BaseService,
    private http: HttpClient,
    private auth: AuthService,
    private addressHelper: AddressHelperService,
    private events: EventsService,
    private policyVehicleAccidentsHelper: PolicyVehicleAccidentsHelperService,
    private ui: UiHelperService,
    private ctx: ContextService,
    private mmobileHelper: MMobileHelperService,
  ) { }

  setVAInitialData(policy: PolicyCar, token: string, sinister?: PolicySinister) {
    const userIsCompany = this.auth.checkUserIsCompany();

    // Particulars use ACI street types that are the VA library default types
    if (userIsCompany) {
      this.vaService.setStreetTypes(this.getRicStreetTypes());
    }
    this.vaService.setPolicyCompanyId(policy.companyId);

    this.vaService.setReturnPages(this.getReturnPages(policy, userIsCompany));

    this.vaService.setContactData(this.getContactData(userIsCompany));

    this.vaService.setContactPeople(this.getContactPeople(policy, userIsCompany));

    this.vaService.setRoadAssistanceEnabled(this.allowRoadAssistance(userIsCompany));

    this.setVARegistryOperations(policy);
    this.dispatchVALogin(token);

    if (sinister) {
      this.setVARepairInitialData(policy, sinister);
    }
  }

  private getReturnPages(policy: PolicyCar, userIsCompany: boolean) {
    const productResumePage = userIsCompany
      ? 'client-area/product-resume'
      : `client-area/product-resume/car?policyNumber=${policy.policyNumber}`;
    return {
      homePath: 'client-area/home',
      productPath: productResumePage
    };
  }

  private getContactData(userIsCompany: boolean) {
    const state = this.auth.getState();
    const user = state?.user || {};
    return {
      name: userIsCompany ? user?.legalRepresentative?.name : user?.name,
      surname1: userIsCompany ? user?.legalRepresentative?.surname1 : user?.surname1,
      surname2: userIsCompany ? user?.legalRepresentative?.surname2 : user?.surname2,
      mobilePhone: userIsCompany ? user?.legalRepresentative?.phone : user?.phone,
      email: userIsCompany ? user?.legalRepresentative?.email : user?.email,
      postalCode: user?.address?.postalCode,
      province: user?.address?.province,
      locality: user?.address?.locality || user?.address?.city,
      street: user?.address?.street,
      streetType: user?.address?.streetType
    };
  }

  private getContactPeople(policy: PolicyCar, userIsCompany: boolean) {
    const { user } = this.auth.getState();
    let contactPeople: ContactPerson[] = [];

    if (userIsCompany) {
      contactPeople = this.mapToContactPeople(policy.forms.filter(policyCarForm => !policyCarForm.socialReason));
      contactPeople.push({
        id: user?.userId,
        name: user?.legalRepresentative?.name,
        surname1: user?.legalRepresentative?.surname1,
        surname2: user?.legalRepresentative?.surname2,
        mobilePhone: user?.legalRepresentative?.phone,
        housePhone: user?.legalRepresentative?.phone,
        email: user?.legalRepresentative?.email,
        documentNumber: user?.legalRepresentative?.documentNumber,
        documentType: this.getDocumentType(user?.legalRepresentative?.documentNumber),
        defaultPerson: false
      } as ContactPerson);
    } else {
      contactPeople = this.mapToContactPeople(policy.forms);
    }
    return contactPeople;
  }

  private mapToContactPeople(forms: PolicyCarForm[]) {
    return forms.map((policyCarForm) => {
      return {
        id: policyCarForm.id,
        name: policyCarForm.name,
        surname1: policyCarForm.surname1,
        surname2: policyCarForm.surname2,
        mobilePhone: policyCarForm.mobilePhone,
        housePhone: policyCarForm.phone,
        email: policyCarForm.email,
        documentNumber: policyCarForm.documentNumber,
        documentType: this.getDocumentType(policyCarForm.documentNumber),
        defaultPerson: policyCarForm.habitualDriver
      } as ContactPerson;
    });
  }

  private getDocumentType(documentNumber: string) {
    if (isValidNie(documentNumber)) {
      return 'NIE';
    } else if (isValidNif(documentNumber)) {
      return 'NIF';
    } else if (validateCif(documentNumber)) {
      return 'CIF';
    } else {
      return 'NIO';
    }
  }

  private getRicStreetTypes() {
    return (this.addressHelper.getRicStreetTypes() || []).map(streetType => {
      return {
        code: streetType.code,
        description: streetType.description,
        carSinisterCode: streetType.carSinisterCode || VEHICLE_ACCIDENTS_DEFAULT_STREET_TYPE,
        useAsDefault: streetType.useAsDefault
      };
    });
  }

  getUpdates(event: VehicleAccidentsEvent) {
    return this.events.getUpdates(event);
  }

  getVAToken(policyNumber: string) {
    const baseUrl = this.base.getPrivateOIMBaseUrl();
    const url = `${baseUrl}/carPolicies/${policyNumber}/vehicleAccidentsToken`;
    const headers = this.base.getPrivateOIMHeaders();
    return this.http.get<{ token: string }>(url, headers);
  }

  private setVARepairInitialData(policy: PolicyCar, sinister: PolicySinister) {
    this.vaService.setRepairInitialData({
      vehiclePlateNumber: policy.plateNumber,
      sinisterExpedientId: sinister.expedientId,
      isProcessContinuous: false
    });
  }

  private dispatchVALogin(token: string) {
    const { user } = this.auth.getState();
    const userIsCompany = this.auth.checkUserIsCompany();
    const info = userIsCompany ? user.legalRepresentative : user || {};
    const finalUser: VehicleAccidentsUser = {
      name: user.name,
      address: user.address,
      surname1: info.surname1,
      surname2: info.surname2,
      phone: info.phone,
      email: info.email,
      birthdate: info.birthdate,
      documentNumberNif: info.documentNumber,
      userCode: user.userCode,
      secondPhone: user.secondPhone,
      nuumaSim: user.nuumaSim
    };
    if (userIsCompany) {
      finalUser.documentNumberCif = user.documentNumber;
    }
    this.vaAuthService.dispatch(
      new VehicleAccidentsLoginAction({ user: finalUser, token })
    );
  }

  private setVARegistryOperations(policy: PolicyCar) {
    const origin = this.or.getOrigin().toString();
    let operations: Map<VehicleAccidentsOperationId, { operationCode: string }>;
    let entityCode: string;
    let group: string;
    let operationsPrefix: string;

    if (this.auth.checkUserIsParticular()) {
      operations = getVAOperationsMap();

      entityCode = '01';
      group = '1';
      operationsPrefix = '8';
    }

    if (this.auth.checkUserIsCompany()) {
      operations = getBusinessVAOperationsMap();

      entityCode = policy.configuration?.cardConfiguration?.ro?.entityCode;
      group = policy.configuration?.cardConfiguration?.ro?.groupingCode;
      operationsPrefix = BUSINESS_OPERATION_CODE_PREFIX;
    }

    this.vaService.setRoConfig({
      appOrigin: 'METS',
      origin,
      enable: true,
      operations,
      operationsPrefix,
      defaultGroupConfigRepair: {
        entityCode,
        groupingCode: group,
        productId: `Matrícula: ${policy.plateNumber}`,
        productCode: policy.department
      },
      defaultGroupConfigReport: {
        entityCode,
        groupingCode: group,
        productId: `Matrícula: ${policy.plateNumber}`,
        productCode: policy.department
      },
      defaultGroupConfigSelectType: {
        entityCode,
        groupingCode: group,
        productId: `Matrícula: ${policy.plateNumber}`,
        productCode: policy.department
      },
      defaultGroupConfigWithoutContrary: {
        entityCode,
        groupingCode: group,
        productId: `Matrícula: ${policy.plateNumber}`,
        productCode: policy.department
      },
      defaultGroupConfigChargingPoint: {
        entityCode,
        groupingCode: group,
        productId: `Matrícula: ${policy.plateNumber}`,
        productCode: policy.department
      }
    });
  }

  async openVA(policy: PolicyCar) {
    await this.ui.presentLoading('client-area/car-sinister-start');
    this.getVAToken(policy.policyNumber).subscribe(
      res => {
        this.ui.dismissLoading('client-area/car-sinister-start');
        this.setVAInitialData(policy, res.token);
        this.nav.to('vehicle-accidents/select-type/step1');
      },
      err => {
        this.ui.dismissLoading('client-area/car-sinister-start');
        this.policyVehicleAccidentsHelper.showAlertError();
      }
    );
  }

  async openVAEraser(policy: PolicyCar, savedSinisters: PolicySinister[]) {
    await this.ui.presentLoading('client-area/car-sinister-start');
    this.getVAToken(policy.policyNumber).subscribe(
      res => {
        this.ui.dismissLoading('client-area/car-sinister-start');
        this.setVAInitialData(policy, res.token);
        this.nav.to('vehicle-accidents/select-type/eraser', {
          policy,
          reportList: savedSinisters
        });
      },
      () => {
        this.ui.dismissLoading('client-area/car-sinister-start');
        this.policyVehicleAccidentsHelper.showAlertError();
      }
    );
  }

  private allowRoadAssistance(userIsCompany) {
    if (userIsCompany) {
      return false;
    }
    return (this.ctx.isApp || this.ctx.isMobileOrTablet)
      ? this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.ROAD_ASSISTANCE_APP)
      : this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.ROAD_ASSISTANCE_WEB);
  }
}
